import { View, StyleSheet } from "react-native";
import { useState } from "react";
import CustomText from "../../components/common/general/CustomText/CustomText";
import CustomSpacing from "../../components/common/layout/CustomSpacing/CustomSpacing";
import { Bot } from "lucide-react";
import { Info } from "@phosphor-icons/react/dist/csr/Info";
import { Upload } from "lucide-react";
import AdminCompanionDocumentCardList from "../../components/adminDashboard/AdminCompanionDocumentCardList/AdminCompanionDocumentCardList";
import { DocumentData } from "../../components/adminDashboard/AdminCompanionDocumentCard/AdminCompanionDocumentCard";

// Mock data for demonstration
const MOCK_DOCUMENTS: DocumentData[] = [
  {
    id: "1",
    filename: "Student Handbook 2025.pdf",
    size: "2.4 MB",
    uploadedDate: "Jan 3, 2026",
    status: "ready",
  },
  {
    id: "2",
    filename: "Financial Aid FAQ.pdf",
    size: "856 KB",
    uploadedDate: "Jan 2, 2026",
    status: "ready",
  },
  {
    id: "3",
    filename: "Campus Services Guide.docx",
    size: "1.2 MB",
    uploadedDate: "Jan 5, 2026",
    status: "processing",
    statusText: "Processing...",
  },
  {
    id: "4",
    filename: "Registration Instructions.pdf",
    size: "445 KB",
    uploadedDate: "Jan 6, 2026",
    status: "uploading",
    statusText: "Uploading...",
  },
];

function AdminCompanionSettingsContainer() {
  const [documents, setDocuments] = useState<DocumentData[]>(MOCK_DOCUMENTS);
  const [isUploadHovered, setIsUploadHovered] = useState(false);

  const handleFileUpload = () => {
    // TODO: Implement file upload logic
    console.log("File upload triggered");
  };

  const handleDelete = (id: string) => {
    setDocuments((prev) => prev.filter((doc) => doc.id !== id));
  };

  return (
    <View style={styles.container}>
      <View style={styles.card}>
        {/* Header Section */}
        <View style={styles.header}>
          <View style={styles.iconContainer}>
            <Bot size={20} color="#7C3AED" />
          </View>
          <View style={styles.headerContent}>
            <CustomText
              text="Support Knowledge Base"
              size="xxl"
              weight="bold"
              useTranslationText={false}
              style={styles.title}
            />
            <CustomSpacing type="vertical" size="xs" />
            <CustomText
              text="Upload documents to power the AI Companion's 'Support' chat. Students can ask questions about policies, procedures, and institutional information."
              size="s"
              useTranslationText={false}
              style={styles.description}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        {/* Upload Area */}
        <div
          role="button"
          tabIndex={0}
          aria-label="Upload documents for Support knowledge base. Accepts PDF, DOCX, TXT files up to 25MB each."
          className="upload-area"
          style={{
            border: isUploadHovered ? "2px dashed #BF84FC" : "2px dashed #D1D5DB",
            borderRadius: 8,
            padding: 32,
            textAlign: "center",
            cursor: "pointer",
            backgroundColor: isUploadHovered ? "#faf5ff80" : "#FFFFFF",
            transition: "border-color 0.2s, background-color 0.2s",
          }}
          onClick={handleFileUpload}
          onMouseEnter={() => setIsUploadHovered(true)}
          onMouseLeave={() => setIsUploadHovered(false)}
          onKeyPress={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleFileUpload();
            }
          }}>
          <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 12 }}>
            <Upload size={32} color="#9CA3AF" />
            <div>
              <View style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
                <CustomText
                  text="Drop files here or click to upload"
                  size="s"
                  weight="medium"
                  useTranslationText={false}
                  style={{ color: "#374151", marginBottom: 4 }}
                />
                <CustomText
                  text="PDF, DOCX, TXT up to 25MB each"
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#6B7280", marginBottom: 4 }}
                />
                <CustomText
                  text="Documents are processed and indexed for AI-powered search"
                  size="xs"
                  useTranslationText={false}
                  style={{ color: "#6B7280" }}
                />
              </View>
            </div>
          </div>
        </div>

        <CustomSpacing type="vertical" size="l" />

        {/* Info Box */}
        <View style={styles.infoBox}>
          <Info size={16} color="#2563EB" weight="regular" style={styles.infoIcon} />
          <View style={styles.infoContent}>
            <CustomText
              text="How it works"
              size="xs"
              weight="bold"
              useTranslationText={false}
              style={styles.infoTitle}
            />
            <CustomText
              text="Uploaded documents are processed and indexed, typically taking about 1 minute. Once ready, the AI Companion can answer student questions using this knowledge base."
              size="xs"
              useTranslationText={false}
              style={styles.infoText}
            />
          </View>
        </View>

        <CustomSpacing type="vertical" size="l" />

        {/* Document List */}
        <AdminCompanionDocumentCardList documents={documents} onDelete={handleDelete} />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  card: {
    backgroundColor: "#FFFFFF",
    borderRadius: 12,
    borderWidth: 1,
    borderColor: "#E5E7EB",
    padding: 24,
  },
  header: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 16,
  },
  iconContainer: {
    width: 40,
    height: 40,
    borderRadius: 16,
    backgroundColor: "#F3E8FF",
    justifyContent: "center",
    alignItems: "center",
    flexShrink: 0,
  },
  headerContent: {
    flex: 1,
  },
  title: {
    color: "#111827",
  },
  description: {
    color: "#475467",
  },
  infoBox: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 8,
    padding: 12,
    backgroundColor: "#EFF6FF",
    borderWidth: 1,
    borderColor: "#BFDBFE",
    borderRadius: 8,
  },
  infoIcon: {
    marginTop: 2,
    flexShrink: 0,
  },
  infoContent: {
    flex: 1,
  },
  infoTitle: {
    color: "#1E40AF",
    marginBottom: 4,
  },
  infoText: {
    color: "#1E40AF",
  },
});

export default AdminCompanionSettingsContainer;
